import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, ButtonGroup, Card, Col, Form, Row,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import api from 'api';
import Loader from 'components/elements/Loader';
import useFeedback from 'hooks/useFeedback';
import { MainRoutes } from 'helpers/routes';

type FacilityStatus = 'Conventional' | 'Contingency' | 'Crisis';

type BedBoardFormData = {
  facilityStatus: FacilityStatus;
  availableIcuAdult: number | '';
  availableIcuPediatric: number | '';
  availableNonIcuAdult: number | '';
  availableNonIcuPediatric: number | '';
  burnCenterSurgeCapacity: number | '';
  hospitalSurgeCapacity: number | '';
};

type BedCapacity = {
  icuAdult?: number;
  icuPediatric?: number;
  nonIcuAdult?: number;
  nonIcuPediatric?: number;
};

type BedBoardStatusApi = {
  facilityStatus: FacilityStatus;
  availableIcuAdult: number | null;
  availableIcuPediatric: number | null;
  availableNonIcuAdult: number | null;
  availableNonIcuPediatric: number | null;
  burnCenterSurgeCapacity: number | null;
  hospitalSurgeCapacity: number | null;
};

type BedBoardCapacityApi = {
  hasStaffingTotals: boolean;
  icuAdult: number | null;
  icuPediatric: number | null;
  nonIcuAdult: number | null;
  nonIcuPediatric: number | null;
};

type Props = {
  onBack?: () => void;
};

const EMPTY_FORM: BedBoardFormData = {
  facilityStatus: 'Conventional',
  availableIcuAdult: '',
  availableIcuPediatric: '',
  availableNonIcuAdult: '',
  availableNonIcuPediatric: '',
  burnCenterSurgeCapacity: '',
  hospitalSurgeCapacity: '',
};

const FACILITY_STATUS_STYLES: Record<FacilityStatus, {
  selectedButton: React.CSSProperties;
  cardBackground: string;
}> = {
  Conventional: {
    selectedButton: { backgroundColor: '#008995', borderColor: '#008995', color: '#fff' },
    cardBackground: '#fff',
  },
  Contingency: {
    selectedButton: { backgroundColor: 'hsl(32 81% 58%)', borderColor: 'hsl(32 81% 58%)', color: '#fff' },
    cardBackground: 'hsl(32 81% 58% / .08)',
  },
  Crisis: {
    selectedButton: { backgroundColor: 'hsl(4 77% 57%)', borderColor: 'hsl(4 77% 57%)', color: '#fff' },
    cardBackground: 'hsl(4 77% 57% / .1)',
  },
};

const UNSELECTED_BUTTON_STYLE: React.CSSProperties = {
  color: '#264b59',
  borderColor: '#ccc',
  backgroundColor: '#fff',
};

const BUTTON_BASE_STYLE: React.CSSProperties = {
  borderWidth: 1,
  borderStyle: 'solid',
};

const getStatusButtonStyle = (
  status: FacilityStatus,
  isSelected: boolean,
): React.CSSProperties => (
  isSelected
    ? { ...BUTTON_BASE_STYLE, ...FACILITY_STATUS_STYLES[status].selectedButton }
    : { ...BUTTON_BASE_STYLE, ...UNSELECTED_BUTTON_STYLE }
);

type BedCountFieldProps = {
  value: number | '';
  total?: number;
  showTotal: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
};

const BedCountField: React.FC<BedCountFieldProps> = ({
  value,
  total,
  showTotal,
  disabled,
  onChange,
}) => {
  const isOverLimit = showTotal
    && total != null
    && value !== ''
    && Number(value) > total;

  return (
    <div
      className={`bed-count-field d-flex align-items-center border rounded w-100 bg-white${isOverLimit ? ' bed-count-field--over-limit' : ''}`}
    >
      <Form.Control
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="bed-count-field__input flex-grow-1"
      />
      {showTotal && total != null && (
      <span className="text-muted flex-shrink-0 px-2 pe-3">
        /
        {' '}
        {total}
      </span>
      )}
    </div>
  );
};

const toFormNumber = (value: number | null | undefined): number | '' => (
  value == null ? '' : value
);

const toApiNumber = (value: number | '') => (value === '' ? null : value);

const mapStatusFromApi = (status: string): FacilityStatus => {
  const normalized = status?.toLowerCase();
  if (normalized === 'contingency') return 'Contingency';
  if (normalized === 'crisis') return 'Crisis';
  return 'Conventional';
};

const mapStatusToForm = (status: BedBoardStatusApi): BedBoardFormData => ({
  facilityStatus: mapStatusFromApi(status.facilityStatus),
  availableIcuAdult: toFormNumber(status.availableIcuAdult),
  availableIcuPediatric: toFormNumber(status.availableIcuPediatric),
  availableNonIcuAdult: toFormNumber(status.availableNonIcuAdult),
  availableNonIcuPediatric: toFormNumber(status.availableNonIcuPediatric),
  burnCenterSurgeCapacity: toFormNumber(status.burnCenterSurgeCapacity),
  hospitalSurgeCapacity: toFormNumber(status.hospitalSurgeCapacity),
});

const mapCapacityFromApi = (data: BedBoardCapacityApi): BedCapacity => ({
  icuAdult: data.icuAdult ?? undefined,
  icuPediatric: data.icuPediatric ?? undefined,
  nonIcuAdult: data.nonIcuAdult ?? undefined,
  nonIcuPediatric: data.nonIcuPediatric ?? undefined,
});

const fetchBurnCenterCapacity = async (): Promise<BedCapacity> => {
  const res = await api.getBedBoardCurrentCapacity();
  return mapCapacityFromApi(res.data);
};

const toStaffingPayload = (capacity: BedCapacity) => ({
  burnStaffedIcuAdultBeds: capacity?.icuAdult ?? null,
  burnStaffedIcuPediatricBeds: capacity?.icuPediatric ?? null,
  burnStaffedNonIcuAdultBeds: capacity?.nonIcuAdult ?? null,
  burnStaffedNonIcuPediatricBeds: capacity?.nonIcuPediatric ?? null,
});

const buildBedBoardPayload = (
  formData: BedBoardFormData,
  capacity: BedCapacity,
) => ({
  facilityStatus: formData.facilityStatus,
  availableIcuAdult: toApiNumber(formData.availableIcuAdult),
  availableIcuPediatric: toApiNumber(formData.availableIcuPediatric),
  availableNonIcuAdult: toApiNumber(formData.availableNonIcuAdult),
  availableNonIcuPediatric: toApiNumber(formData.availableNonIcuPediatric),
  ...toStaffingPayload(capacity),
  burnCenterSurgeCapacity: toApiNumber(formData.burnCenterSurgeCapacity),
  hospitalSurgeCapacity: toApiNumber(formData.hospitalSurgeCapacity),
});

const BedBoard: React.FC<Props> = ({ onBack }) => {
  const navigate = useNavigate();
  const goBack = onBack ?? (() => navigate(MainRoutes.HOME));
  const { setError, setSuccess } = useFeedback();

  const [formData, setFormData] = useState<BedBoardFormData>(EMPTY_FORM);
  const [capacity, setCapacity] = useState<BedCapacity>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recordingNoChange, setRecordingNoChange] = useState(false);

  const loadBedBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, capacityRes] = await Promise.all([
        api.getBedBoardCurrent(),
        api.getBedBoardCurrentCapacity(),
      ]);
      setFormData(mapStatusToForm(statusRes.data));
      setCapacity(mapCapacityFromApi(capacityRes.data));
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to load bed board data.');
      setFormData(EMPTY_FORM);
      setCapacity({});
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    loadBedBoard();
  }, [loadBedBoard]);

  const handleStatusChange = (status: FacilityStatus) => {
    setFormData((prev) => ({ ...prev, facilityStatus: status }));
  };

  const handleNumberChange = (field: keyof BedBoardFormData, value: string) => {
    const parsed = value === '' ? '' : Math.max(0, parseInt(value, 10));
    setFormData((prev) => ({ ...prev, [field]: parsed }));
  };

  const handleCancel = () => {
    goBack();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const burnCenterCapacity = await fetchBurnCenterCapacity();
      await api.updateBedBoardCurrent(buildBedBoardPayload(formData, burnCenterCapacity));
      setSuccess('Bed board saved successfully.');
      goBack();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to save bed board.');
    } finally {
      setSaving(false);
    }
  };

  const handleNoChange = async () => {
    setRecordingNoChange(true);
    try {
      const burnCenterCapacity = await fetchBurnCenterCapacity();
      await api.recordBedBoardNoChange(toStaffingPayload(burnCenterCapacity));
      setSuccess('Bed board recorded with no changes.');
      goBack();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to record no change.');
    } finally {
      setRecordingNoChange(false);
    }
  };

  const isBusy = loading || saving || recordingNoChange;

  if (loading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <div>

      <Card
        className="border rounded-3 p-4"
        style={{ backgroundColor: FACILITY_STATUS_STYLES[formData.facilityStatus].cardBackground }}
      >
        <Row className="mb-2 g-2">
          <Col xs={12} sm={12} md={6} lg={7} xl={8}>
            <h5 className="fw-bold mb-2">Bed Board Input</h5>
          </Col>
          <Col
            xs={12}
            sm={12}
            md={6}
            lg={5}
            xl={4}
            className="d-flex flex-column"
          >
            <ButtonGroup className="bed-board-facility-status">
              {(['Conventional', 'Contingency', 'Crisis'] as FacilityStatus[]).map((status) => (
                <Button
                  key={status}
                  variant="light"
                  size="sm"
                  onClick={() => handleStatusChange(status)}
                  className="d-flex justify-content-center align-items-center gap-1"
                  disabled={isBusy}
                  style={getStatusButtonStyle(status, formData.facilityStatus === status)}
                >
                  {status === 'Contingency' && <FontAwesomeIcon icon={faTriangleExclamation} size="xs" />}
                  {status === 'Crisis' && <FontAwesomeIcon icon={faLightbulb} size="xs" />}
                  {status}
                </Button>
              ))}
            </ButtonGroup>
            <small className="text-muted mt-1 align-self-end fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
              FACILITY STATUS
            </small>
          </Col>
        </Row>

        <h6 className="fw-bold my-3">Available Burn Beds</h6>

        <Row className="g-2 mb-2">
          <Col xs={12} sm={3} md={3} className="d-none d-sm-block" />
          <Col xs={6} sm={4} md={4}>
            <span className="fw-semibold text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
              Adult
            </span>
          </Col>
          <Col xs={6} sm={4} md={4}>
            <span className="fw-semibold text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
              Pediatric
            </span>
          </Col>
        </Row>

        <Row className="g-2 mb-3 align-items-center">
          <Col xs={12} sm={3} md={3} className="fw-semibold mb-2 mb-sm-0">
            ICU
          </Col>
          <Col xs={6} sm={4} md={4}>
            <BedCountField
              value={formData.availableIcuAdult}
              total={capacity.icuAdult}
              showTotal
              disabled={isBusy}
              onChange={(v) => handleNumberChange('availableIcuAdult', v)}
            />
          </Col>
          <Col xs={6} sm={4} md={4}>
            <BedCountField
              value={formData.availableIcuPediatric}
              total={capacity.icuPediatric}
              showTotal
              disabled={isBusy}
              onChange={(v) => handleNumberChange('availableIcuPediatric', v)}
            />
          </Col>
        </Row>

        <Row className="g-2 mb-4 align-items-center">
          <Col xs={12} sm={3} md={3} className="fw-semibold mb-2 mb-sm-0">
            Non ICU
          </Col>
          <Col xs={6} sm={4} md={4}>
            <BedCountField
              value={formData.availableNonIcuAdult}
              total={capacity.nonIcuAdult}
              showTotal
              disabled={isBusy}
              onChange={(v) => handleNumberChange('availableNonIcuAdult', v)}
            />
          </Col>
          <Col xs={6} sm={4} md={4}>
            <BedCountField
              value={formData.availableNonIcuPediatric}
              total={capacity.nonIcuPediatric}
              showTotal
              disabled={isBusy}
              onChange={(v) => handleNumberChange('availableNonIcuPediatric', v)}
            />
          </Col>
        </Row>

        <h6 className="fw-bold my-3">Surge Capacity</h6>

        <Row className="g-3 mb-4">
          <Col xs={12} md={7} lg={6}>
            <Form.Group className="mb-0">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                Burn center surge capacity (Contingency status)
              </Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={formData.burnCenterSurgeCapacity}
                onChange={(e) => handleNumberChange('burnCenterSurgeCapacity', e.target.value)}
                disabled={isBusy}
                className="w-100"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={7} lg={6}>
            <Form.Group className="mb-0">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                Hospital surge capacity (Crisis status)
              </Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={formData.hospitalSurgeCapacity}
                onChange={(e) => handleNumberChange('hospitalSurgeCapacity', e.target.value)}
                disabled={isBusy}
                className="w-100"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex flex-wrap justify-content-end gap-2">

          <div className="flex-1 align-content-center" style={{ flexGrow: 1 }}>
            <a href="/training" style={{ fontSize: '0.8rem' }} target="_blank">
              Bed Capacity Field Definitions
            </a>
          </div>

          <Button
            variant="cancel"
            onClick={handleCancel}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleNoChange}
            disabled={isBusy}
          >
            {recordingNoChange ? 'Recording…' : 'No Change'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isBusy}
            style={{ backgroundColor: '#008995', borderColor: '#008995', color: '#fff' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default BedBoard;
